import React, { useState, useEffect } from "react";
import "./redemptionform.scss";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { redemptionSchema } from "../../../utils/validationSchemas";
import Loader from "../../common/Loader";
import PrimaryButton from "../../common/PrimaryButton";
import Input from "../../common/FormInputs";
import { ReactComponent as RefreshIcon } from "../../../Assets/Icons/refreshIcon.svg";
import { ReactComponent as DoubleTickIcon } from "../../../Assets/Icons/doubletick.svg";
import {
  checkRedemptionCode,
  getVerificationStats,
} from "../../../services/merchantService";
import RedemptionResultModal from "./RedemptionResultModal";
import VerificationHistoryModal from "./VerificationHistoryModal";

const RedemptionFormPage = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.merchantReducer?.loading);
  const verificationStats = useSelector(
    (state) => state?.merchantReducer?.verificationStats,
  );
  const [verifyClicked, setVerifyClicked] = useState(false);
  const [offer, setOffer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [modalResult, setModalResult] = useState("success");
  const [memberName, setMemberName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    dispatch(getVerificationStats({ silent: true }));
  }, [dispatch]);

  const refreshStats = (delayMs = 1000) => {
    setTimeout(() => {
      dispatch(getVerificationStats({ silent: true }));
    }, delayMs);
  };

  const formik = useFormik({
    initialValues: { code: "" },
    validationSchema: redemptionSchema,
    onSubmit: async (values) => {
      // Clear previous results before checking new code
      setOffer(null);
      setMemberName("");
      setErrorMsg("");

      try {
        const checkPromise = await dispatch(
          checkRedemptionCode({ code: values?.code?.toUpperCase() }),
        )?.unwrap();
        if (checkPromise?.success === true) {
          setOffer(checkPromise?.data);
          setMemberName(checkPromise?.data?.redeemingMember || "");
          setModalResult("success");
          setIsModalOpen(true);
          // Refresh stats after a short delay so backend has time to record the redemption
          refreshStats(1200);
        } else {
          setModalResult("error");
          setErrorMsg(
            checkPromise?.message || "No active offer found for this code.",
          );
          setIsModalOpen(true);
          // Refresh on failed attempts too
          refreshStats(800);
        }
      } catch (e) {
        setModalResult("error");
        setErrorMsg(e?.message || "An error occurred during verification.");
        setIsModalOpen(true);
      }
    },
  });

  const { code } = formik.values;
  const { handleChange, handleBlur, handleSubmit, errors, touched, resetForm } =
    formik;
  const handleCodeChange = (e) => {
    e.target.value = e.target.value.toUpperCase();
    handleChange(e);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    resetForm();
    // Clear state on close to prevent stale data if modal is reopened quickly
    setOffer(null);
    setMemberName("");
    setErrorMsg("");
    // Refresh again on close to ensure latest data is shown
    dispatch(getVerificationStats({ silent: true }));
  };

  // Helper for relative time if not provided by backend
  const formatTime = (date) => {
    if (!date) return "";
    const now = new Date();
    const past = new Date(date);
    const diffInMs = now - past;
    const diffInMins = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMins / 60);

    if (diffInMins < 1) return "Just now";
    if (diffInMins < 60) return `${diffInMins} mins ago`;
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    return past.toLocaleDateString();
  };

  const recentVerifications = verificationStats?.recentVerifications || [];
  const displayVerifications = recentVerifications.slice(0, 3);
  const hasMoreHistory = recentVerifications.length > 3;

  return (
    <div className="w-full h-full bg-white rounded-[10px] shadow-[0px_0px_50px_rgba(0,0,0,0.08)] border border-[#EAEAEA] flex flex-col items-center p-8 sm:p-14 lg:p-20 overflow-auto">
      {loading === true && <Loader />}
      <div className="w-full max-w-[1050px]">
        {/* Header Section */}
        <div className="mb-6 text-left w-full pl-2">
          <h1 className="text-[25px] sm:text-[30px] font-[600] text-[#303030] font-poppins">
            Optional Redemption{" "}
            <span className="text-[#00ADEF]">Verification</span>
          </h1>
          <p className="mt-0 text-[17px] text-[#767676] font-poppins font-[400]">
            This optional feature lets you track redemption code usage and
            verify member codes when needed.
          </p>
        </div>

        {/* HOW IT WORKS Section */}
        <div className="bg-white rounded-[24px] shadow-[0px_8px_40px_rgba(0,0,0,0.04)] border border-[#EAEAEA] w-full p-8 sm:p-12 mb-12">
          <p className="text-[12px] font-poppins font-[600] text-[#A3A3A3] tracking-[2px] text-center mb-10 uppercase">
            HOW IT WORKS (OPTIONAL)
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            <div className="flex flex-col items-center">
              <div className="w-[48px] h-[48px] rounded-full bg-[#00ADEF] text-white flex items-center justify-center font-[600] text-[18px] mb-6 font-poppins shadow-md">
                1
              </div>
              <h3 className="font-[600] text-[#303030] text-[16px] mb-2 font-poppins">
                Member presents code
              </h3>
              <p className="text-[#767676] text-[14px] font-poppins max-w-[200px] leading-relaxed">
                Member shows their digital code
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-[48px] h-[48px] rounded-full bg-[#00ADEF] text-white flex items-center justify-center font-[600] text-[18px] mb-6 font-poppins shadow-md">
                2
              </div>
              <h3 className="font-[600] text-[#303030] text-[16px] mb-2 font-poppins">
                Verify for peace of mind
              </h3>
              <p className="text-[#767676] text-[14px] font-poppins max-w-[220px] leading-relaxed">
                Merchant enters code to confirm validity and member identity
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-[48px] h-[48px] rounded-full bg-[#20C997] text-white flex items-center justify-center font-[600] text-[18px] mb-6 font-poppins shadow-md">
                3
              </div>
              <h3 className="font-[600] text-[#303030] text-[16px] mb-2 font-poppins">
                Confident transaction
              </h3>
              <p className="text-[#767676] text-[14px] font-poppins max-w-[220px] leading-relaxed">
                Merchant sees member details and confirmation that the code is
                live
              </p>
            </div>
          </div>
        </div>

        {/* Verify a Code Section */}
        <div className="bg-white rounded-[24px] shadow-[0px_8px_40px_rgba(0,0,0,0.04)] border border-[#EAEAEA] w-full p-8 sm:p-16 text-center mb-12">
          <h2 className="text-[32px] font-[600] text-[#303030] mb-4 font-poppins">
            Verify a Code (Optional)
          </h2>
          <p className="text-[#767676] text-[18px] mb-12 max-w-[600px] mx-auto font-poppins leading-relaxed">
            Optionally check a code before honoring an offer to confirm it is
            valid and see who is redeeming it.
          </p>

          <form
            className="max-w-[770px] mx-auto flex flex-col items-center"
            onSubmit={handleSubmit}
          >
            <div className="w-full relative mb-6">
              <Input
                type="text"
                label=""
                placeholder="ENTER 6-DIGIT REDEMPTION CODE"
                width="100%"
                radius="14px"
                inputClass="text-center uppercase font-[600] !text-[20px] !tracking-[10px] !bg-[#F9FAFB] !border !border-[#EAEAEA] !text-[#303030] placeholder:text-[#D1D1D1] placeholder:!text-[20px] placeholder:!tracking-[10px] !h-[80px] transition-all focus:!bg-white focus:!border-[#00ADEF] focus:!ring-1 focus:!ring-[#00ADEF]"
                name="code"
                value={code}
                handleChange={handleCodeChange}
                handleBlur={handleBlur}
                error={errors.code}
                touched={touched.code}
                maxLength={6}
                InputDivClass="w-full"
                errorClass="!text-left w-full pl-2 mt-1"
              />
            </div>

            <PrimaryButton
              type="submit"
              className="w-full font-poppins text-[18px] font-[600] !h-[65px] rounded-[14px] bg-[#00ADEF] hover:bg-[#0099D4] flex items-center justify-center shadow-none mt-2 transition-all active:scale-[0.99]"
              label={
                <div className="flex items-center justify-center gap-3">
                  <DoubleTickIcon style={{ width: 26, height: 28 }} />
                  Verify Redemption Code
                </div>
              }
            />
          </form>
        </div>

        {/* Statistics Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-12">
          {/* Total Offers Redeemed */}
          <div className="bg-white rounded-[24px] shadow-[0px_8px_40px_rgba(0,0,0,0.04)] border border-[#EAEAEA] p-7 flex items-center justify-between">
            <div className="flex flex-col">
              <p className="text-[12px] font-poppins font-[600] text-[#A3A3A3] mb-1">
                Total Offers Redeemed
              </p>
              <h4 className="text-[38px] font-[600] text-[#303030] font-poppins">
                {verificationStats?.totalOffersRedeemed || 0}
              </h4>
            </div>
            <div className="w-[58px] h-[58px] bg-[#F0FAFF] rounded-[15px] flex items-center justify-center">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 20V10M12 20V4M6 20V14"
                  stroke="#00ADEF"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          {/* Verified Today */}
          <div className="bg-white rounded-[24px] shadow-[0px_8px_40px_rgba(0,0,0,0.04)] border border-[#EAEAEA] p-7 flex items-center justify-between">
            <div className="flex flex-col">
              <p className="text-[12px] font-poppins font-[600] text-[#A3A3A3] mb-1">
                Verified Today
              </p>
              <h4 className="text-[38px] font-[600] text-[#303030] font-poppins">
                {verificationStats?.verifiedToday ??
                  verificationStats?.todayRedeemed ??
                  0}
              </h4>
            </div>
            <div className="w-[58px] h-[58px] bg-[#E7F9F0] rounded-[15px] flex items-center justify-center">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 12L10 15L17 8M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                  stroke="#20C997"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Recent Verifications Section */}
        <div className="w-full">
          <div className="flex items-center justify-between mb-6 px-2">
            <h3 className="text-[22px] font-[600] text-[#303030] font-poppins">
              Recent Verifications
            </h3>
            {hasMoreHistory && (
              <button
                onClick={() => setIsHistoryModalOpen(true)}
                className="text-[#00ADEF] text-[14px] font-[600] font-poppins hover:underline"
              >
                View All History
              </button>
            )}
          </div>

          <div className="bg-white rounded-[24px] shadow-[0px_8px_40px_rgba(0,0,0,0.04)] border border-[#EAEAEA] overflow-hidden">
            {displayVerifications.length > 0 ? (
              <div className="divide-y divide-[#F2F4F7]">
                {displayVerifications.map((item, index) => (
                  <div
                    className={`flex items-center justify-between p-6 hover:bg-[#F9FAFB] transition-colors`}
                  >
                    <div className="flex items-center gap-5">
                      <div
                        className={`w-[48px] h-[48px] rounded-full flex items-center justify-center ${item?.status === "success" || item?.status === "valid" ? "bg-[#E7F9F0]" : "bg-[#FFF2F2]"}`}
                      >
                        {item?.status === "success" ||
                        item?.status === "valid" ? (
                          <svg
                            width="20"
                            height="15"
                            viewBox="0 0 20 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M18.3333 1.66663L6.66667 13.3333L1.66667 8.33329"
                              stroke="#20C997"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <circle
                              cx="9"
                              cy="9"
                              r="8"
                              stroke="#FF4D4F"
                              strokeWidth="2"
                            />
                            <path
                              d="M9 6V9"
                              stroke="#FF4D4F"
                              strokeWidth="2"
                              strokeLinecap="round"
                            />
                            <path
                              d="M9 12H9.01"
                              stroke="#FF4D4F"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                      </div>
                      <div className="flex flex-col">
                        <p className="text-[17px] font-[600] text-[#303030] font-poppins uppercase tracking-[1px]">
                          {item?.code || "CODE-UNKNOWN"}
                        </p>
                        <p className="text-[14px] text-[#8C8C8C] font-poppins">
                          {!item?.memberName || item?.memberName === "Unknown"
                            ? "Unclaimed"
                            : `Redeemed by ${item.memberName}`}{" "}
                          • {formatTime(item?.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div
                      className={`px-4 py-1.5 rounded-full text-[12px] font-[700] font-poppins uppercase tracking-[1px] ${item?.status === "success" || item?.status === "valid" ? "bg-[#E7F9F0] text-[#20C997]" : "bg-[#FFF2F2] text-[#FF4D4F]"}`}
                    >
                      {item?.status === "success" || item?.status === "valid"
                        ? "VALID"
                        : "INVALID"}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center">
                <p className="text-[#8C8C8C] font-poppins">
                  No recent verifications found.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <RedemptionResultModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        result={modalResult}
        memberName={memberName}
        offerTitle={offer?.offerTitle}
        offerDiscountType={offer?.offerDiscountType}
        error={errorMsg}
      />

      <VerificationHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        history={recentVerifications}
        formatTime={formatTime}
      />
    </div>
  );
};

export default RedemptionFormPage;
